import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types and hooks of `docs/dev/api-connections.md` (Einstellungen 04–13, Beratung 08). */

export type ConnectionProvider = 'google' | 'outlook' | 'mail' | 'whatsapp' | 'caldav';
export type ConnectionPurpose = 'mail' | 'calendar' | 'messaging';
export type ConnectionStatus = 'connecting' | 'connected' | 'credentials_required' | 'disconnected' | 'error';
export type CalendarRole = 'ignore' | 'block' | 'block_and_show';

export interface ConnectionCalendar {
  id: string;
  externalId: string;
  name: string;
  role: CalendarRole;
  isBookingCalendar: boolean;
  writable: boolean;
}

export interface ConnectionItem {
  id: string;
  userId: string | null;
  isSupportMailbox: boolean;
  provider: ConnectionProvider;
  purposes: ConnectionPurpose[];
  displayName: string | null;
  identifier: string | null;
  status: ConnectionStatus;
  statusLabel: string;
  lastSyncedAt: string | null;
  error: string | null;
  progressText: string | null;
  /** CalDAV: the calendar-home URL BRIMO resolved; null for every other provider. */
  serverUrl: string | null;
  calendars: ConnectionCalendar[];
}

export interface ConnectionsResponse {
  items: ConnectionItem[];
  /** Unipile credentials present: the Google/Outlook/IMAP wizard is available (409 otherwise). */
  configured: boolean;
  /** The organization's WhatsApp bridge is configured: start/refresh of a session are available (409 otherwise). */
  whatsappConfigured: boolean;
}

export interface LinkResponse {
  connectionId: string;
  url: string;
}

export interface ImapBody {
  preset?: 'imap' | 'icloud';
  email: string;
  imapHost?: string;
  imapPort?: number;
  imapUser?: string;
  password: string;
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  ssl?: boolean;
}

/** `POST /connections/caldav` — `icloud` fixes the server to `https://caldav.icloud.com` (Apple-ID + app-specific password). */
export interface CaldavBody {
  preset: 'caldav' | 'icloud';
  serverUrl?: string;
  username: string;
  password: string;
}

export type WhatsappState = 'none' | 'starting' | 'qr' | 'expired' | 'pairing' | 'connected' | 'disconnected';

export interface WhatsappPending {
  id: string;
  contactId: string | null;
  contactName: string | null;
  title: string;
  sendAt: string | null;
  status: 'scheduled' | 'failed';
  /** Hold reason of a scheduled message (send window, limits) or the failure text. */
  error: string | null;
}

export interface WhatsappStatus {
  state: WhatsappState;
  connectionId: string | null;
  status: ConnectionStatus | null;
  qrDataUrl: string | null;
  expiresAt: string | null;
  phoneE164: string | null;
  deviceName: string | null;
  connectedSince: string | null;
  lastSeenAt: string | null;
  sentToday: number;
  receivedToday: number;
  pendingMessages: number;
  pending: WhatsappPending[];
  progressText: string | null;
  error: string | null;
}

/** `POST /connections/whatsapp` and `/refresh` (202): the bridge session was started, with the code when it is already there. */
export interface WhatsappStartResponse {
  connectionId: string;
  state: 'starting' | 'qr' | 'connected';
  qrDataUrl: string | null;
  expiresAt: string | null;
}

export const NOT_CONFIGURED_MESSAGE = 'Verbindungen sind in dieser Umgebung nicht eingerichtet.';

export const connectionsKey = [...queryRoots.connections] as const;
export const whatsappKey = [...queryRoots.connections, 'whatsapp'] as const;
export const supportMailboxKey = [...queryRoots.connections, 'support-mailbox'] as const;

export function connectionKey(id: string) {
  return [...queryRoots.connections, 'item', id] as const;
}

/** The user's connections with the environment flags (`configured`, `whatsappConfigured`). */
export function useConnections() {
  return useQuery({
    queryKey: connectionsKey,
    queryFn: () => api<ConnectionsResponse>('/connections'),
  });
}

/** One connection, polled every 3 s while it is `connecting` (Einstellungen 07). */
export function useConnection(id: string | null) {
  return useQuery({
    queryKey: connectionKey(id ?? 'none'),
    queryFn: () => api<ConnectionItem>(`/connections/${id ?? ''}`),
    enabled: id !== null,
    refetchInterval: (query) => (query.state.data?.status === 'connecting' ? 3000 : false),
  });
}

export function useSupportMailbox(enabled: boolean) {
  return useQuery({
    queryKey: supportMailboxKey,
    queryFn: () => api<{ connection: ConnectionItem | null }>('/connections/support-mailbox'),
    enabled,
    select: (data) => data.connection,
  });
}

/**
 * Polls every 2 s while the bridge boots the session, a code is shown (it rotates — the latest data URL is
 * rendered) or the phone pairs (Einstellungen 08/10, D-55). `expired` waits for the user („Neuen Code laden").
 */
export function useWhatsappStatus(userId?: string) {
  return useQuery({
    queryKey: userId ? [...whatsappKey, userId] : whatsappKey,
    queryFn: () =>
      api<WhatsappStatus>(userId ? `/connections/whatsapp?userId=${userId}` : '/connections/whatsapp'),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'starting' || state === 'qr' || state === 'pairing' ? 2000 : false;
    },
  });
}

function useInvalidateConnections() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: connectionsKey });
  };
}

export function useLinkConnection() {
  return useMutation({
    mutationFn: (body: {
      provider: 'google' | 'outlook';
      purposes: Array<'mail' | 'calendar'>;
      support?: boolean;
    }) =>
      body.support
        ? api<LinkResponse>('/connections/support-mailbox/link', {
            method: 'POST',
            body: { provider: body.provider },
          })
        : api<LinkResponse>('/connections/link', {
            method: 'POST',
            body: { provider: body.provider, purposes: body.purposes },
          }),
  });
}

export function useCreateImapConnection() {
  const invalidate = useInvalidateConnections();
  return useMutation({
    mutationFn: (input: { body: ImapBody; support?: boolean }) =>
      api<ConnectionItem>(input.support ? '/connections/support-mailbox/imap' : '/connections/imap', {
        method: 'POST',
        body: input.body,
      }),
    onSuccess: invalidate,
  });
}

/** E05 „Apple iCloud Kalender" / „CalDAV" → step 2 „Verbinden": the row is created `connected` with its calendars. */
export function useCreateCaldavConnection() {
  const invalidate = useInvalidateConnections();
  return useMutation({
    mutationFn: (body: CaldavBody) => api<ConnectionItem>('/connections/caldav', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

/** E04 „Neu anmelden" of a CalDAV row: verifies the new password and stores it. */
export function useReconnectCaldavConnection() {
  const invalidate = useInvalidateConnections();
  return useMutation({
    mutationFn: (input: { id: string; password: string }) =>
      api<ConnectionItem>(`/connections/${input.id}/caldav/reconnect`, {
        method: 'POST',
        body: { password: input.password },
      }),
    onSuccess: invalidate,
  });
}

export function useReconnectConnection() {
  return useMutation({
    mutationFn: (id: string) =>
      api<LinkResponse>(`/connections/${id}/reconnect`, { method: 'POST', body: {} }),
  });
}

export function useDeleteConnection() {
  const invalidate = useInvalidateConnections();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/connections/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function usePatchCalendar() {
  const invalidate = useInvalidateConnections();
  return useMutation({
    mutationFn: (input: {
      connectionId: string;
      calendarId: string;
      body: { role?: CalendarRole; isBookingCalendar?: boolean; writable?: boolean };
    }) =>
      api<ConnectionCalendar>(`/connections/${input.connectionId}/calendars/${input.calendarId}`, {
        method: 'PATCH',
        body: input.body,
      }),
    onSuccess: invalidate,
  });
}

/**
 * Applies the 202 answer of start/refresh to the cached status at once (the page switches to `starting`,
 * `qr` or `connected` without waiting for the next poll), then refetches everything under `connections`.
 */
function useApplyWhatsappStart() {
  const queryClient = useQueryClient();
  return async (result: WhatsappStartResponse) => {
    queryClient.setQueryData<WhatsappStatus>(whatsappKey, (current) => {
      const base: WhatsappStatus = current ?? {
        state: 'none',
        connectionId: null,
        status: null,
        qrDataUrl: null,
        expiresAt: null,
        phoneE164: null,
        deviceName: null,
        connectedSince: null,
        lastSeenAt: null,
        sentToday: 0,
        receivedToday: 0,
        pendingMessages: 0,
        pending: [],
        progressText: null,
        error: null,
      };
      return {
        ...base,
        state: result.state,
        connectionId: result.connectionId,
        status: result.state === 'connected' ? 'connected' : 'connecting',
        qrDataUrl: result.qrDataUrl,
        expiresAt: result.expiresAt,
        progressText: null,
        error: null,
      };
    });
    await queryClient.invalidateQueries({ queryKey: connectionsKey });
  };
}

/** E04 „QR-Code anzeigen" / E08 „Neuen Code erzeugen" / E12 „Neu verbinden": starts (or re-attaches) the bridge session. */
export function useStartWhatsapp() {
  const apply = useApplyWhatsappStart();
  return useMutation({
    mutationFn: () => api<WhatsappStartResponse>('/connections/whatsapp', { method: 'POST', body: {} }),
    onSuccess: apply,
  });
}

/** E09 „Neuen Code laden": restarts the 60-second window with the bridge's current code. */
export function useRefreshWhatsapp() {
  const apply = useApplyWhatsappStart();
  return useMutation({
    mutationFn: () =>
      api<WhatsappStartResponse>('/connections/whatsapp/refresh', { method: 'POST', body: {} }),
    onSuccess: apply,
  });
}

export function useEndWhatsapp() {
  const invalidate = useInvalidateConnections();
  return useMutation({
    mutationFn: () => api<void>('/connections/whatsapp', { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** „Postfach und Kalender" — the purposes of a card subtitle (Einstellungen 04). */
export function purposesText(purposes: readonly ConnectionPurpose[]): string {
  const parts: string[] = [];
  if (purposes.includes('mail')) parts.push('Postfach');
  if (purposes.includes('calendar')) parts.push('Kalender');
  if (purposes.includes('messaging')) parts.push('WhatsApp');
  if (parts.length === 0) return 'Verbindung';
  if (parts.length === 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1] ?? ''}`;
}

/** Card title: „Google Workspace · j.weiler@brimo.de". */
export function connectionTitle(item: ConnectionItem): string {
  const name = item.displayName ?? providerLabel(item.provider);
  return item.identifier ? `${name} · ${item.identifier}` : name;
}

export function providerLabel(provider: ConnectionProvider): string {
  switch (provider) {
    case 'google':
      return 'Google Workspace';
    case 'outlook':
      return 'Microsoft / Outlook';
    case 'mail':
      return 'IMAP / SMTP';
    case 'whatsapp':
      return 'WhatsApp';
    case 'caldav':
      return 'CalDAV';
  }
}

export const ICLOUD_CALENDAR_LABEL = 'Apple iCloud Kalender';

/** The iCloud preset of a CalDAV connection (Apple-ID + app-specific password, D-56). */
export function isIcloudCalendar(item: Pick<ConnectionItem, 'provider' | 'displayName'>): boolean {
  return item.provider === 'caldav' && item.displayName === ICLOUD_CALENDAR_LABEL;
}

/** „Privat, Feiertage" — the calendar names of a connection (Beratung 08). */
export function calendarNames(item: Pick<ConnectionItem, 'calendars'>): string {
  return item.calendars.map((calendar) => calendar.name).join(', ');
}

/** The calendar of a connection that the booking mirror and conflict check use. */
export function bookingCalendarOf(item: ConnectionItem): ConnectionCalendar | null {
  return (
    item.calendars.find((calendar) => calendar.isBookingCalendar) ??
    item.calendars.find((calendar) => calendar.role !== 'ignore') ??
    item.calendars[0] ??
    null
  );
}

export function isCalendarConnection(item: ConnectionItem): boolean {
  return (
    (item.provider === 'google' || item.provider === 'outlook' || item.provider === 'caldav') &&
    item.purposes.includes('calendar')
  );
}

export function isMailConnection(item: ConnectionItem): boolean {
  return item.provider !== 'whatsapp' && item.purposes.includes('mail');
}

/** „+49 171 2298440" — the display form of an E.164 number (country code · area/mobile prefix · rest). */
export function formatPhone(e164: string | null): string {
  if (!e164) return '—';
  const match = /^\+(49|43|41)(\d{3})(\d+)$/.exec(e164);
  if (!match) return e164;
  return `+${match[1]} ${match[2]} ${match[3]}`;
}
